import { z } from "zod";

// =============================================================================
// Pagination Input Schema
// =============================================================================

/**
 * Cursor-based pagination input parameters
 * - cursor: Opaque cursor string (base64 encoded createdAt + id)
 * - limit: Number of items per page (default 20, max 100)
 * - all: When true, returns all items without pagination
 */
export const paginationInputSchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().min(1).max(100).default(20),
  all: z
    .enum(["true", "false", "1", "0"])
    .transform((v) => v === "true" || v === "1")
    .optional(),
});

export type PaginationInput = z.infer<typeof paginationInputSchema>;

// =============================================================================
// Pagination Output Types
// =============================================================================

/**
 * Pagination metadata returned in API responses
 */
export interface PaginationMeta {
  nextCursor: string | null;
  hasMore: boolean;
  /** Total rows matching the filters (ignoring the cursor); only endpoints that count provide it */
  total?: number;
}

/**
 * Generic paginated response wrapper
 */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMeta;
}

// =============================================================================
// Cursor Utilities
// =============================================================================

/**
 * Cursor structure for encoding/decoding
 * Uses createdAt + id for uniqueness and deterministic ordering
 */
export interface CursorData {
  createdAt: string; // ISO string
  id: string; // UUID
}

/**
 * Encode cursor data to base64url string
 */
export const encodeCursor = (data: CursorData): string => {
  return Buffer.from(JSON.stringify(data)).toString("base64url");
};

/**
 * Decode cursor string to cursor data
 * Returns null if cursor is invalid
 */
export const decodeCursor = (cursor: string): CursorData | null => {
  try {
    const decoded = Buffer.from(cursor, "base64url").toString("utf-8");
    const parsed = JSON.parse(decoded);
    if (parsed.createdAt && parsed.id) {
      return parsed as CursorData;
    }
    return null;
  } catch {
    return null;
  }
};
